import React, { useState, useMemo, useEffect, useCallback } from "react";
import ReactSelect from "react-select";
import {
  Button,
  Card,
  DescriptionList,
  EmptyState,
  Field,
  Loader,
  Select,
  Table,
  TextInput,
} from "../../../../../ui";
import { Actions, Callout, Dialog, FormGrid, StudentStatusBadge } from "../ui-pending";
import { dsReactSelectStyles } from "../ui-pending/ReactSelectStyles";
import { toast } from "react-toastify";
import { createCourseExtensionPaymentLink, getCourseExtensionList, deleteCourseExtension } from "../../../../../Api/apiUtils";
import { MixpanelEvents, trackEvent } from "../../../../../utils/mixpanelTracking";
import "./payment-panels.scss";

const CURRENCY_OPTIONS = ["INR", "USD", "CAD", "AUD", "GBP", "SGD", "AED", "EUR"].map((c) => ({ value: c, label: c }));

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

interface CourseExtensionProps {
  studentId: string;
  profileData: any;
  courseData: any;
}

const CourseExtension: React.FC<CourseExtensionProps> = ({
  studentId,
  profileData,
  courseData,
}) => {
  const [modal, setModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<{
    value: string;
    label: string;
    courseName: string;
  } | null>(null);
  const [expiryDate, setExpiryDate] = useState("");
  const [reason, setReason] = useState("");
  const [currency, setCurrency] = useState<"INR" | "USD" | "CAD" | "AUD" | "GBP" | "SGD" | "AED" | "EUR">("INR");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdLink, setCreatedLink] = useState<{
    short_url?: string;
    payment_link_id?: string;
    id?: string;
    status?: string;
  } | null>(null);
  const [extensionList, setExtensionList] = useState<any[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<any | null>(null);

  const fetchList = useCallback(async () => {
    if (!studentId) return;
    setListLoading(true);
    try {
      const res = await getCourseExtensionList(profileData.userId);
      const data = Array.isArray(res?.data) ? res.data : [];
      const forStudent = data.filter((item: any) => String(item.student_id) === String(studentId));
      setExtensionList(forStudent);
    } catch {
      setExtensionList([]);
    } finally {
      setListLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const paidCourses = useMemo(() => {
    if (!Array.isArray(courseData)) return [];
    return courseData.filter((c: any) => !c.isDemoCourse);
  }, [courseData]);

  const courseOptions = useMemo(
    () =>
      paidCourses.map((c: any) => ({
        value: String(c.courseId),
        label: c.courseName || `Course ${c.courseId}`,
        courseName: c.courseName || "",
      })),
    [paidCourses]
  );

  const resetForm = () => {
    setSelectedCourse(null);
    setExpiryDate("");
    setReason("");
    setCurrency("INR");
    setAmount("");
  };

  const handleOpen = () => {
    resetForm();
    setModal(true);
    trackEvent(MixpanelEvents.COURSE_EXTENSION_MODAL_OPENED, {
      page: "Student Profile - Course Extension",
      studentId: studentId ?? "",
    });
  };

  const handleClose = () => {
    setModal(false);
    resetForm();
    setCreatedLink(null);
    fetchList();
  };

  const copyToClipboard = (text: string, context?: "created_modal" | "share_link") => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => {
        toast.success("Copied to clipboard");
        trackEvent(MixpanelEvents.COURSE_EXTENSION_LINK_COPIED, {
          page: "Student Profile - Course Extension",
          studentId: studentId ?? "",
          context: context ?? "created_modal",
        });
      },
      () => toast.error("Failed to copy")
    );
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const getDeletedBy = () => (typeof localStorage !== "undefined" && localStorage.getItem("loginUser")) || "Admin";

  const openDeleteModal = (item: any) => {
    if (item?.id) setItemToDelete(item);
  };

  const closeDeleteModal = () => setItemToDelete(null);

  const confirmDelete = async () => {
    if (!itemToDelete?.id) return;
    setDeletingId(itemToDelete.id);
    try {
      await deleteCourseExtension(itemToDelete.id, getDeletedBy());
      toast.success("Deleted successfully.");
      trackEvent(MixpanelEvents.COURSE_EXTENSION_DELETED, {
        page: "Student Profile - Course Extension",
        studentId: studentId ?? "",
        extensionId: itemToDelete.id,
        courseName: itemToDelete?.course_name,
      });
      closeDeleteModal();
      fetchList();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to delete.";
      toast.error(msg);
      trackEvent(MixpanelEvents.COURSE_EXTENSION_DELETE_FAILED, {
        page: "Student Profile - Course Extension",
        studentId: studentId ?? "",
        extensionId: itemToDelete?.id,
        error: msg,
      });
    } finally {
      setDeletingId(null);
    }
  };

  const formatCreatedOn = (dateStr: string) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
      const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
      return `${date}, ${time}`;
    } catch {
      return dateStr;
    }
  };

  const handleCreate = async () => {
    if (!profileData) {
      toast.error("Profile data not loaded.");
      return;
    }
    if (!selectedCourse) {
      toast.warning("Please select a course.");
      return;
    }
    if (!expiryDate.trim()) {
      toast.warning("Please select an expiry date.");
      return;
    }
    if (!reason.trim()) {
      toast.warning("Please enter a reason.");
      return;
    }
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      toast.warning("Please enter a valid amount.");
      return;
    }

    const token = getCookie("jwtToken");
    const authToken = token?.startsWith("Bearer ") ? token : `Bearer ${token || ""}`;

    const expiryFormatted = `${expiryDate.trim()} 00:00:00`;
    const name = [profileData.firstName, profileData.lastName].filter(Boolean).join(" ").trim() || "Student";

    const payload = {
      studentId: String(profileData.studentId ?? studentId),
      userId: profileData.userId,
      currency,
      firstName: profileData.firstName || "",
      lastName: profileData.lastName || "",
      name,
      gender: profileData.gender || "Male",
      ageGroup: profileData.ageGroup || "",
      studentType: profileData.studentType || "PAID",
      userType: profileData.userType || "PAID",
      isOffline: false,
      centreId: "",
      email: profileData.emailId?.trim() || "",
      countryCode: profileData.countryCode || "+91",
      contact: profileData.mobileNumber || "",
      otherPayment: "course_extension_fee",
      amount: numAmount,
      totalPrice: numAmount,
      token: authToken,
      discountPercent: 0,
      discountedPrice: 0,
      createdBy: (typeof localStorage !== "undefined" && localStorage.getItem("loginUser")) || "Admin",
      state: profileData.state || "",
      refNumber: "",
      paymentMode: "Razorpay",
      courseId: selectedCourse.value,
      courseName: selectedCourse.courseName || selectedCourse.label,
      expiryDate: expiryFormatted,
      modificationReason: reason.trim(),
    };

    setLoading(true);
    try {
      const result = await createCourseExtensionPaymentLink(payload);
      setCreatedLink(result || null);
      toast.success("Course extension payment link created successfully.");
      trackEvent(MixpanelEvents.COURSE_EXTENSION_LINK_CREATED, {
        page: "Student Profile - Course Extension",
        studentId: studentId ?? "",
        courseId: selectedCourse?.value,
        courseName: selectedCourse?.courseName ?? selectedCourse?.label,
        currency,
        amount: numAmount,
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to create payment link.";
      toast.error(msg);
      trackEvent(MixpanelEvents.COURSE_EXTENSION_LINK_CREATE_FAILED, {
        page: "Student Profile - Course Extension",
        studentId: studentId ?? "",
        error: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ds-stack course-extension-page">
      <Card
        title="Course extensions"
        subtitle="Payment links that extend a course's expiry date."
        flush
        actions={
          <Button variant="primary" onClick={handleOpen}>
            Create Package
          </Button>
        }
      >
        {listLoading ? (
          <Loader label="Loading course extensions…" />
        ) : extensionList.length > 0 ? (
          <Table caption="Course extensions">
            <thead>
              <tr>
                <th>Course Name</th>
                {/* <th>Username</th> */}
                <th className="ds-num">Amount</th>
                <th>Status</th>
                <th>Created On</th>
                <th><span className="visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {extensionList.map((item: any) => (
                <tr key={item.id}>
                  <td>{item.course_name || "—"}</td>
                  {/* <td>{item.username || "—"}</td> */}
                  <td className="ds-num payment-panel__nowrap">{item.currency || "INR"} {item.amount ?? "—"}</td>
                  <td>
                    {item.payment_status || item.payment_link_status ? (
                      <StudentStatusBadge status={item.payment_status || item.payment_link_status} />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="payment-panel__nowrap">{formatCreatedOn(item.created_at)}</td>
                  <td>
                    {
                      item.payment_link && (
                        <Actions>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              if (item.payment_link) {
                                trackEvent(MixpanelEvents.COURSE_EXTENSION_SHARE_LINK_CLICKED, {
                                  page: "Student Profile - Course Extension",
                                  studentId: studentId ?? "",
                                  extensionId: item.id,
                                  courseName: item.course_name,
                                });
                                copyToClipboard(item.payment_link, "share_link");
                              } else {
                                toast.info("No payment link available");
                              }
                            }}
                          >
                            Share Link
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="payment-panel__remove"
                            loading={deletingId === item.id}
                            onClick={() => openDeleteModal(item)}
                          >
                            Delete
                          </Button>
                        </Actions>
                      )
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState
            title="No course extensions"
            description="No course extension records for this student. Use Create Package to send a payment link."
          />
        )}
      </Card>

      <Dialog
        isOpen={modal}
        onClose={handleClose}
        title={createdLink ? "Payment link created" : "Course Extension – Payment Link"}
        actions={
          createdLink ? (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  trackEvent(MixpanelEvents.COURSE_EXTENSION_CREATE_ANOTHER, {
                    page: "Student Profile - Course Extension",
                    studentId: studentId ?? "",
                  });
                  setCreatedLink(null);
                  resetForm();
                }}
              >
                Create another
              </Button>
              <Button variant="primary" onClick={handleClose}>
                Done
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleCreate} loading={loading}>
                {loading ? "Creating..." : "Create"}
              </Button>
            </>
          )
        }
      >
        {createdLink ? (
          <>
            <Callout tone="success">Payment link created successfully.</Callout>
            {createdLink.short_url && (
              <Field label="Short URL">
                {(ctl) => (
                  <div className="payment-panel__copy-row">
                    <TextInput
                      {...ctl}
                      type="text"
                      readOnly
                      value={createdLink.short_url}
                    />
                    <Button
                      variant="secondary"
                      onClick={() => copyToClipboard(createdLink.short_url!, "created_modal")}
                    >
                      Copy
                    </Button>
                  </div>
                )}
              </Field>
            )}
            {createdLink.payment_link_id && (
              <DescriptionList items={[{ label: "Payment link ID", value: <code>{createdLink.payment_link_id}</code> }]} />
            )}
          </>
        ) : (
          <>
            <Field label="Course">
              {(ctl) => (
                <ReactSelect
                  inputId={ctl.id}
                  aria-describedby={ctl["aria-describedby"]}
                  placeholder="Select course"
                  options={courseOptions}
                  value={selectedCourse}
                  onChange={(v) => setSelectedCourse(v)}
                  isClearable
                  styles={dsReactSelectStyles<{ value: string; label: string; courseName: string }>()}
                />
              )}
            </Field>
            <Field label="Expiry Date">
              {(ctl) => (
                <TextInput
                  {...ctl}
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              )}
            </Field>
            <Field label="Reason">
              {(ctl) => (
                <TextInput
                  {...ctl}
                  type="text"
                  placeholder="Modification reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
            </Field>
            <FormGrid min={180}>
              <Field label="Currency">
                {(ctl) => (
                  <Select
                    {...ctl}
                    value={currency}
                    onChange={(e) =>
                      setCurrency(
                        e.target.value as
                          | "INR"
                          | "USD"
                          | "CAD"
                          | "AUD"
                          | "GBP"
                          | "SGD"
                          | "AED"
                          | "EUR"
                      )
                    }
                    options={CURRENCY_OPTIONS}
                  />
                )}
              </Field>
              <Field label="Amount">
                {(ctl) => (
                  <TextInput
                    {...ctl}
                    type="number"
                    numeric
                    min={0}
                    step={currency === "USD" ? 0.01 : 1}
                    placeholder={currency === "USD" ? "Amount (USD)" : "Amount (INR)"}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                )}
              </Field>
            </FormGrid>
          </>
        )}
      </Dialog>

      <Dialog
        isOpen={!!itemToDelete}
        onClose={closeDeleteModal}
        title="Delete this course extension?"
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              loading={!!itemToDelete && deletingId === itemToDelete.id}
            >
              {itemToDelete && deletingId === itemToDelete.id ? "Deleting..." : "Delete"}
            </Button>
          </>
        }
      >
        Are you sure you want to delete this course extension record?
      </Dialog>
    </div>
  );
};

export default CourseExtension;
